import * as _ from 'lodash';
import { Component, Input, ViewEncapsulation } from '@angular/core';
import { getTranslationKeyForAttribute, AttributeGroup } from 'helpers';
import { formatDeliveryDateToText } from 'app/b2b-order/helpers/format-delivery-date';

@Component({
    selector: 'fc-pdp-attributes',
    templateUrl: './product-attributes.component.html',
    styleUrls: ['./product-attributes.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PdpAttributesComponent {
    @Input() set manufacturerAttributes(manufacturerAttributes) {
        this.metadata = this.formatMetadata(manufacturerAttributes);
        this.hasMetadata =
            !_.isEmpty(this.metadata.mainAttributes) ||
            !_.isEmpty(this.metadata.description) ||
            !_.isEmpty(this.metadata.extraAttributes);
    }

    @Input() set fcAttributes(fcAttributes) {
        this.fashionCloudAttributes = this.formatFashionCloudAttributes(fcAttributes);
        this.hasFashionCloudAttributes = !_.isEmpty(this.fashionCloudAttributes);
    }

    @Input() set certificatesAttributes(certificatesAttributes) {
        this.sustainabilityAttributes = this.formatSustainabilityAttributes(certificatesAttributes);
        this.hasSustainabilityAttributes = !_.isEmpty(this.sustainabilityAttributes);
    }

    public metadata: ProductMetadata;
    public fashionCloudAttributes: Attribute[];
    public hasMetadata: boolean;
    public hasFashionCloudAttributes: boolean;
    public sustainabilityAttributes: any;
    public hasSustainabilityAttributes: boolean;

    public formatSustainabilityAttributesKey(certificate, value: string) {
        const attributeKey = _.findKey(certificate, (property) => property === value);
        if (attributeKey) return getTranslationKeyForAttribute(AttributeGroup.SUSTAINABILITYATTRIBUTES, attributeKey);
    }

    public isEmpty(value) {
        return _.isEmpty(value) && !_.isNumber(value) && !_.isBoolean(value);
    }

    private formatMetadata(manufacturerAttributes) {
        if (_.isEmpty(manufacturerAttributes)) {
            return {
                mainAttributes: [],
                extraAttributes: []
            };
        }

        const mainAttributeKeys = [
            'name',
            'colorName',
            'colorCode',
            'category',
            'size',
            'targetGroup',
            'material',
            'price',
            'currency'
        ];

        const productAttributes = _.keys(manufacturerAttributes);
        const extraAttributesKeys = _.difference(productAttributes, [...mainAttributeKeys, 'description']);

        const descriptionValue = _.get(manufacturerAttributes, 'description');
        const description = descriptionValue
            ? {
                  name: 'description',
                  value: descriptionValue,
                  translationKey: getTranslationKeyForAttribute(AttributeGroup.ManufacturerAttributes, 'description')
              }
            : null;

        const deliveryDates = manufacturerAttributes.deliveryDates
            ? [].concat(manufacturerAttributes.deliveryDates).map(formatDeliveryDateToText).join(', ')
            : null;

        return {
            mainAttributes: this.getAttributesWithValue(
                manufacturerAttributes,
                mainAttributeKeys,
                AttributeGroup.ManufacturerAttributes
            ),
            extraAttributes: this.getAttributesWithValue(
                { ..._.omit(manufacturerAttributes, 'certificates'), deliveryDates },
                extraAttributesKeys,
                AttributeGroup.ManufacturerAttributes
            ),
            description
        };
    }

    private formatFashionCloudAttributes(fashionCloudAttributes): any {
        return !_.isEmpty(fashionCloudAttributes)
            ? this.getAttributesWithValue(
                  fashionCloudAttributes,
                  _.keys(fashionCloudAttributes),
                  AttributeGroup.FcAttributes
              )
            : {};
    }

    private getAttributesWithValue(
        attributes: any,
        attributeKeys: string[],
        attributeGroup: AttributeGroup
    ): Attribute[] {
        return _.reduce(
            attributeKeys,
            (attributesWithValue, attribute) => {
                const value = _.get(attributes, attribute);
                if (value) {
                    return [
                        ...attributesWithValue,
                        {
                            name: attribute,
                            value,
                            translationKey: getTranslationKeyForAttribute(attributeGroup, attribute)
                        }
                    ];
                }
                return attributesWithValue;
            },
            []
        );
    }

    private formatSustainabilityAttributes(sustainabilityAttributes) {
        const keysArray = _.keys(sustainabilityAttributes);
        const nestedKeys = keysArray.map((nested: any) => _.keys(sustainabilityAttributes[nested])).flat();
        const uniqueNestedKeys = [...new Set(nestedKeys)];
        if (keysArray.length > 0 && uniqueNestedKeys.length > 0) {
            return {
                keys: keysArray,
                nestedKeys: uniqueNestedKeys,
                ...sustainabilityAttributes
            };
        } else return {};
    }
}

interface ProductMetadata {
    mainAttributes: Attribute[];
    description?: Attribute;
    extraAttributes: Attribute[];
}

interface Attribute {
    name: string;
    value: string;
    translationKey: string;
}
